import { useEffect, useState } from "react";
import api, { eur, eurFull, downloadPdf } from "@/lib/api";
import { FilePdf, Printer, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Reporting() {
  const [kpis, setKpis] = useState(null);
  const [deals, setDeals] = useState([]);
  const [syn, setSyn] = useState(null);
  const [dl, setDl] = useState(false);

  useEffect(() => {
    api.get("/dashboard/kpis").then((r) => setKpis(r.data));
    api.get("/deals").then((r) => setDeals(r.data));
    api.get("/reports/synergies").then((r) => setSyn(r.data));
  }, []);

  const downloadServerPdf = async () => {
    setDl(true);
    try { await downloadPdf("/reports/investisseurs/pdf", "rapport_investisseurs.pdf"); }
    catch { toast.error("Erreur génération PDF"); } finally { setDl(false); }
  };

  const acquisitions = deals.filter((d) => ["Closing", "Intégration"].includes(d.stage));

  return (
    <div>
      <header className="h-16 px-8 flex items-center justify-between border-b border-border bg-white sticky top-0 z-10 print:hidden">
        <h1 className="font-head font-bold text-xl flex items-center gap-2"><FilePdf size={20} className="text-primary" /> Reporting investisseurs</h1>
        <div className="flex items-center gap-2">
          <button data-testid="download-server-pdf-btn" onClick={downloadServerPdf} disabled={dl}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-50">
            <DownloadSimple size={16} /> {dl ? "Génération…" : "Télécharger le PDF"}
          </button>
          <button data-testid="print-report-btn" onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-border hover:bg-secondary">
            <Printer size={16} /> Imprimer
          </button>
        </div>
      </header>

      <div className="p-8 max-w-4xl mx-auto">
        <div className="bg-white border border-border p-10">
          <div className="flex items-center justify-between border-b border-border pb-5 mb-6">
            <div>
              <div className="overline text-primary">AI Assurances · Rapport d'acquisition</div>
              <h2 className="font-head font-black text-2xl mt-1">Portefeuille d'acquisitions</h2>
            </div>
            <div className="text-right text-xs text-muted-foreground font-mono">{new Date().toLocaleDateString("fr-FR")}</div>
          </div>

          {kpis && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-8">
                {[
                  ["Acquisitions", kpis.acquisitions],
                  ["Valeur totale", eur(kpis.valeur_acquisitions)],
                  ["EBITDA / Résultat cumulé", eur(Math.round(kpis.ca_cumule * 0.18))],
                  ["ROI prévisionnel", `${kpis.roi_previsionnel}%`],
                  ["CA cumulé cibles", eur(kpis.ca_cumule)],
                  ["Commissions cumulées", eur(kpis.commissions_cumulees)],
                  ["Cibles dans le pipeline", kpis.cibles_identifiees],
                  ["LOI signées", kpis.loi_signees],
                ].map(([l, v]) => (
                  <div key={l} className="bg-white p-4">
                    <div className="overline text-muted-foreground">{l}</div>
                    <div className="font-mono text-xl font-semibold mt-1">{v}</div>
                  </div>
                ))}
              </div>

              <h3 className="font-head font-bold text-base mb-3">Création de valeur · ROI &amp; EBITDA</h3>
              <table className="w-full text-sm mb-6" data-testid="value-creation-table">
                <tbody>
                  {[
                    ["EBITDA estimé", "18 % du CA cumulé des cibles", eur(Math.round(kpis.ca_cumule * 0.18))],
                    ["Valeur de sortie estimée", "3,0× commissions récurrentes", eur(Math.round(kpis.commissions_cumulees * 3))],
                    ["Capital investi (acquisitions)", "Valorisation des cibles acquises", eur(kpis.valeur_acquisitions)],
                    ["Valeur créée (théorique)", "Valeur de sortie − capital investi", eur(Math.round(kpis.commissions_cumulees * 3) - kpis.valeur_acquisitions)],
                  ].map(([l, m, v]) => (
                    <tr key={l} className="border-b border-border">
                      <td className="py-2 font-medium">{l}</td>
                      <td className="py-2 text-muted-foreground text-xs">{m}</td>
                      <td className="py-2 text-right font-mono font-semibold">{v}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-primary">
                    <td className="py-2 font-semibold text-primary">ROI prévisionnel</td>
                    <td className="py-2 text-muted-foreground text-xs">Valeur créée / capital investi</td>
                    <td className="py-2 text-right font-mono font-bold text-primary">{kpis.roi_previsionnel} %</td>
                  </tr>
                </tbody>
              </table>

              <h3 className="font-head font-bold text-base mb-3">Acquisitions réalisées</h3>
              {acquisitions.length === 0 ? (
                <p className="text-sm text-muted-foreground mb-6">Aucune acquisition finalisée (stade Closing/Intégration).</p>
              ) : (
                <table className="w-full text-sm mb-6">
                  <thead><tr className="border-b border-border text-muted-foreground">
                    <th className="text-left overline py-2">Cabinet</th><th className="text-left overline py-2">Ville</th>
                    <th className="text-right overline py-2">Commissions</th><th className="text-right overline py-2">Valorisation</th>
                  </tr></thead>
                  <tbody>
                    {acquisitions.map((d) => (
                      <tr key={d.id} className="border-b border-border">
                        <td className="py-2 font-medium">{d.company_nom}</td>
                        <td className="py-2 text-muted-foreground">{d.ville}</td>
                        <td className="py-2 text-right font-mono">{eurFull(d.commissions)}</td>
                        <td className="py-2 text-right font-mono">{eurFull(d.valorisation)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h3 className="font-head font-bold text-base mb-3">Synergies consolidées · réalisées vs estimées</h3>
              {syn && syn.breakdown && syn.breakdown.length > 0 ? (
                <div data-testid="synergies-consolidated">
                  <div className="flex items-end justify-between mb-1">
                    <span className="text-sm">Réalisées : <span className="font-mono font-semibold text-foreground">{eur(syn.total_realise)}</span></span>
                    <span className="text-sm text-muted-foreground">Estimées : <span className="font-mono">{eur(syn.total_estime)}</span></span>
                  </div>
                  <div className="h-2.5 bg-secondary rounded-full overflow-hidden mb-1">
                    <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, syn.taux_global)}%` }} />
                  </div>
                  <div className="text-xs text-muted-foreground mb-4">{syn.taux_global}% des synergies estimées sont réalisées.</div>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border text-muted-foreground">
                      <th className="text-left overline py-2">Cabinet</th><th className="text-right overline py-2">Estimées</th>
                      <th className="text-right overline py-2">Réalisées</th><th className="text-right overline py-2">Taux</th>
                    </tr></thead>
                    <tbody>
                      {syn.breakdown.map((b) => (
                        <tr key={b.company_id} className="border-b border-border">
                          <td className="py-2 font-medium">{b.company_nom}</td>
                          <td className="py-2 text-right font-mono">{eurFull(b.estime)}</td>
                          <td className="py-2 text-right font-mono text-emerald-700">{eurFull(b.realise)}</td>
                          <td className="py-2 text-right font-mono">{b.taux}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Aucune synergie suivie pour l'instant. Renseignez les synergies réalisées dans l'onglet
                  « Intégration » de chaque cabinet acquis pour alimenter ce rapport. Estimation indicative actuelle :{" "}
                  <span className="font-mono font-semibold text-foreground">{eur(Math.round(kpis.commissions_cumulees * 0.08))}</span> par an.
                </p>
              )}
            </>
          )}

          <div className="mt-10 pt-5 border-t border-border text-xs text-muted-foreground font-mono">
            Document généré automatiquement par AI Assurances — confidentiel.
          </div>
        </div>
      </div>
    </div>
  );
}
